import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { fetchUserAdvertisers } from "../services/dcm/advertisers";
import { setAuthError } from "../actions/app/global";

// Shared loader for "which advertisers is this user linked to".
//
// Every caller used to inline the same fetch with a `.catch(console.log)`, which
// is how a stepper ended up with a permanently greyed-out Next button and
// nothing on screen to explain it: an expired token (or any 5xx) left the list
// empty forever, the effect ran once on mount so nothing retried, and the only
// way out was a full re-login. Statuses are explicit here so callers can tell
// "you have no advertiser access" apart from "we couldn't find out", and every
// caller gets a retry that doesn't need a page reload.
export const ADVERTISER_STATUS = {
  IDLE: "idle", // not requested (caller passed enabled: false)
  LOADING: "loading",
  OK: "ok",
  EMPTY: "empty", // fetch succeeded, the account genuinely has no advertisers
  ERROR: "error" // fetch failed — we don't know either way
};

export const ADVERTISER_MESSAGES = {
  EMPTY:
    "No user advertisers found linked to your account. Please contact the site administrator before proceeding.",
  UNAUTHORIZED:
    "Your session has expired. Please sign in again to load your advertisers.",
  ERROR: "Couldn't load the advertisers linked to your account."
};

export const useUserAdvertisers = ({ enabled = true } = {}) => {
  const dispatch = useDispatch();
  const [advertisers, setAdvertisers] = useState([]);
  const [status, setStatus] = useState(
    enabled ? ADVERTISER_STATUS.LOADING : ADVERTISER_STATUS.IDLE
  );
  const [message, setMessage] = useState("");

  // Steppers unmount on navigation and this resolves late — don't set state
  // into a dead component.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!enabled) {
      setStatus(ADVERTISER_STATUS.IDLE);
      return;
    }

    setStatus(ADVERTISER_STATUS.LOADING);
    setMessage("");

    try {
      const response = await fetchUserAdvertisers();
      if (!isMountedRef.current) return;

      const list = Array.isArray(response) ? response : [];
      if (list.length > 0) {
        setAdvertisers(list);
        setStatus(ADVERTISER_STATUS.OK);
        return;
      }

      setAdvertisers([]);
      setStatus(ADVERTISER_STATUS.EMPTY);
      setMessage(ADVERTISER_MESSAGES.EMPTY);
    } catch (e) {
      if (!isMountedRef.current) return;

      setAdvertisers([]);
      setStatus(ADVERTISER_STATUS.ERROR);

      if (e && e.message === "UNAUTHORIZED") {
        // Matches how the dcm/* actions report a 401.
        dispatch(setAuthError());
        setMessage(ADVERTISER_MESSAGES.UNAUTHORIZED);
      } else {
        setMessage(ADVERTISER_MESSAGES.ERROR);
      }
    }
  }, [enabled, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    advertisers,
    status,
    message,
    retry: load,
    isReady: status === ADVERTISER_STATUS.OK,
    isLoading: status === ADVERTISER_STATUS.LOADING,
    hasFailed: status === ADVERTISER_STATUS.ERROR
  };
};

export default useUserAdvertisers;
